import * as React from 'react';
import { cn } from '@/lib/utils';

export interface TrapezoidFrameProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** ニックネーム（最大10文字、表示は最大4文字+省略記号） */
  nickname?: string;
  /** 背景色 */
  backgroundColor?: string;
  /** ラベルのテキスト色 */
  labelTextColor?: string;
  /** フレームの幅（px） */
  width?: number;
  /** フレームの高さ */
  height?: string | number;
  /** 左側の斜めの角度（px） */
  slantSize?: number;
  /** コンテンツのパディング */
  contentPadding?: string;
}

/**
 * ニックネームを省略して表示（最大4文字+...）
 */
function truncateNickname(nickname?: string): string {
  if (!nickname) return '';

  const upperName = nickname.toUpperCase();
  if (upperName.length <= 4) return upperName;

  return `${upperName.slice(0, 4)}...`;
}

/**
 * TrapezoidFrame - 台形ラベル付きフレームコンポーネント
 *
 * 右上に台形のラベルが付いたフォルダのようなフレームで、
 * "For [NICKNAME]" を表示し、コンテンツを囲みます。
 * ニックネームは最大4文字で表示され、それを超える場合は省略記号(...)が付きます。
 *
 * @example
 * ```tsx
 * // デフォルト
 * <TrapezoidFrame nickname="Taka">
 *   <p>コンテンツ</p>
 * </TrapezoidFrame>
 *
 * // 長いニックネーム（省略されます）
 * <TrapezoidFrame nickname="Alexander">
 *   <p>表示: "ALEX..."</p>
 * </TrapezoidFrame>
 *
 * // 色をカスタマイズ
 * <TrapezoidFrame
 *   nickname="User"
 *   backgroundColor="#F8F8F8"
 *   labelTextColor="#616161"
 * >
 *   <p>コンテンツ</p>
 * </TrapezoidFrame>
 *
 * // Tailwindのクラスで追加スタイル
 * <TrapezoidFrame nickname="Test" className="border-2">
 *   <p>コンテンツ</p>
 * </TrapezoidFrame>
 * ```
 */
export const TrapezoidFrame = React.forwardRef<
  HTMLDivElement,
  TrapezoidFrameProps
>(
  (
    {
      className,
      nickname,
      backgroundColor = '#F8F8F8',
      labelTextColor = '#616161',
      height = 'auto',
      slantSize = 15,
      contentPadding = '1rem',
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const displayName = truncateNickname(nickname);

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-center w-full relative',
          className,
        )}
        style={style}
        {...props}
      >
        {/* "For NICKNAME" ラベル */}
        {nickname && (
          <div
            className="absolute top-0 right-0 flex items-center justify-center"
            style={{
              width: '127px',
              height: '22px',
              backgroundColor,
              clipPath: `polygon(${slantSize}px 0, 100% 0, 100% 100%, 0 100%)`,
              transform: 'translate(0, -100%)',
            }}
          >
            <span
              className="text-[12px] font-normal"
              style={{ color: labelTextColor }}
            >
              For {displayName}
            </span>
          </div>
        )}

        {/* フレーム本体 */}
        <div
          className="flex flex-col gap-8 relative"
          style={{
            borderRadius: '6px 0 6px 6px',
            backgroundColor,
            width: '100%',
            height: typeof height === 'number' ? `${height}px` : height,
            padding: contentPadding,
          }}
        >
          {children}
        </div>
      </div>
    );
  },
);

TrapezoidFrame.displayName = 'TrapezoidFrame';
